import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import { Type, Layers, Medal, Crop, Loader2, Play, CircleGauge, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChunkDetailsModal } from './chunk-details-modal';
import type {
  IRetrievalQueryResult,
  IRetrievalAnalysisData,
} from '../../types/agent.service.type';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { getMetricDescription, getMetricDots, getQueryTypeLabel } from './utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';

type QueryPanelProps = {
  vectorQuery: string;
  setVectorQuery: (value: string) => void;
  hasResults: boolean;
  handleRunQuery: () => void;
  activeTab: string;
  setActiveTab: (value: string) => void;
  isLoading: boolean;
  results: IRetrievalQueryResult[];
  analysisData: IRetrievalAnalysisData | null;
  groundTruthTexts?: string[];
  hasEmptyGroundTruthError?: boolean;
};

export const QueryPanel = ({
  vectorQuery,
  setVectorQuery,
  hasResults,
  handleRunQuery,
  activeTab,
  setActiveTab,
  isLoading,
  results,
  analysisData,
  groundTruthTexts,
  hasEmptyGroundTruthError,
}: QueryPanelProps) => {
  const { t } = useTranslation();
  const [selectedChunk, setSelectedChunk] = useState<{
    id: string;
    title: string;
    url?: string;
    content: string;
    tokens: number;
    layers: number;
    score: number;
    tags: string[];
  } | null>(null);

  const hasValidGroundTruth =
    groundTruthTexts &&
    groundTruthTexts.length > 0 &&
    groundTruthTexts.some((text) => text.trim() !== '');

  return (
    <div className="flex flex-1 flex-col gap-4">
      <Card className="flex-shrink-0">
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="query-input" className="mb-2 block text-sm font-medium">
                {t('QUERY')}
              </Label>
              <Input
                id="query-input"
                value={vectorQuery}
                onChange={(e) => setVectorQuery(e.target.value)}
                placeholder={t('RETRIEVAL_QUERY_PLACEHOLDER')}
                className="h-10"
              />
            </div>
            <div className="pt-6">
              <Button
                size="default"
                onClick={handleRunQuery}
                disabled={isLoading || !vectorQuery.trim() || hasEmptyGroundTruthError}
                className="h-10 max-w-24 px-6"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('RUNNING')}
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    {t('RUN')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="flex-1 overflow-hidden">
        <CardContent className="flex h-full flex-col">
          {hasResults ? (
            <div className="flex flex-1 flex-col overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full flex-col">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <TabsList className="w-fit">
                    {results.map((result) => (
                      <TabsTrigger key={result.query_type} value={result.query_type}>
                        {getQueryTypeLabel(result.query_type, t)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <div className="flex gap-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <div className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-[#FFE27A] dark:border-amber-600 bg-[#FFFDF0] dark:bg-amber-900/20 px-4 py-1 transition-shadow hover:shadow-md">
                          <CircleGauge className="h-6 w-6 text-medium-emphasis" />
                          <span className="text-sm font-bold text-medium-emphasis">
                            {t('ADEQUACY')}{' '}
                            <b>{analysisData?.score_of_adequate_context.toFixed(2)}</b>
                          </span>
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-96" align="end">
                        <div className="text-sm font-normal text-medium-emphasis">
                          {analysisData &&
                          parseFloat(analysisData.score_of_adequate_context.toFixed(2)) >= 0.65
                            ? t('ADEQUACY_CONTEXT_SUFFICIENT', {
                                score: analysisData.score_of_adequate_context.toFixed(2),
                              })
                            : t('ADEQUACY_CONTEXT_INSUFFICIENT', {
                                score: analysisData?.score_of_adequate_context.toFixed(2),
                              })}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {results.find((r) => r.query_type === activeTab)?.metrics && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <div className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-[#FFE27A] dark:border-amber-600 bg-[#FFFDF0] dark:bg-amber-900/20 px-4 py-1 transition-shadow hover:shadow-md">
                            <Sparkles className="h-6 w-6 text-medium-emphasis" />
                            <span className="text-sm font-bold text-medium-emphasis">{t('QUALITY')}</span>
                            <div className="flex flex-col items-center">
                              <span className="text-xl font-bold text-medium-emphasis">
                                {results
                                  .find((r) => r.query_type === activeTab)
                                  ?.metrics.f1_score.toFixed(2)}
                              </span>
                              <div className="flex gap-0.5">
                                {getMetricDots(
                                  results.find((r) => r.query_type === activeTab)?.metrics
                                    .f1_score || 0
                                ).map((filled, i) => (
                                  <div
                                    key={i}
                                    className={`h-2 w-2 rounded-full ${
                                      filled ? 'bg-green-500' : 'bg-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-96" align="end">
                          <div className="space-y-6">
                            {(() => {
                              const metrics = results.find(
                                (r) => r.query_type === activeTab
                              )?.metrics;
                              if (!metrics) return null;
                              return (
                                <>
                                  <div className="flex items-start justify-between">
                                    <div className="space-y-2">
                                      <div className="text-sm font-medium text-medium-emphasis">
                                        {t('QUALITY')}
                                      </div>
                                      <div className="text-base font-medium text-high-emphasis">
                                        {metrics.f1_score.toFixed(2)}
                                      </div>
                                      <div className="flex gap-1">
                                        {getMetricDots(metrics.f1_score).map((filled, i) => (
                                          <div
                                            key={i}
                                            className={`h-2 w-2 rounded-full ${
                                              filled ? 'bg-green-500' : 'bg-gray-300'
                                            }`}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                    <div className="max-w-[200px] text-sm text-muted-foreground">
                                      {getMetricDescription(metrics.f1_score, 'f1', t)}
                                    </div>
                                  </div>

                                  <div className="flex items-start justify-between">
                                    <div className="space-y-2">
                                      <div className="text-sm font-medium text-medium-emphasis">
                                        {t('ACCURACY')}
                                      </div>
                                      <div className="text-base font-medium text-high-emphasis">
                                        {metrics.precision.toFixed(2)}
                                      </div>
                                      <div className="flex gap-1">
                                        {getMetricDots(metrics.precision).map((filled, i) => (
                                          <div
                                            key={i}
                                            className={`h-2 w-2 rounded-full ${
                                              filled ? 'bg-green-500' : 'bg-gray-300'
                                            }`}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                    <div className="max-w-[200px] text-sm text-muted-foreground">
                                      {getMetricDescription(metrics.precision, 'precision', t)}
                                    </div>
                                  </div>

                                  <div className="flex items-start justify-between">
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-medium-emphasis">
                                          {t('COVERAGE')}
                                        </span>
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                                                ?
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>{t('COVERAGE_TOOLTIP')}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </div>
                                      <div className="text-base font-medium text-high-emphasis">
                                        {metrics.recall}/
                                        {results.find((r) => r.query_type === activeTab)?.documents
                                          .length || 0}
                                      </div>
                                      <div className="flex gap-1">
                                        {getMetricDots(metrics.recall).map((filled, i) => (
                                          <div
                                            key={i}
                                            className={`h-2 w-2 rounded-full ${
                                              filled ? 'bg-yellow-500' : 'bg-gray-300'
                                            }`}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                    <div className="max-w-[200px] text-sm text-muted-foreground">
                                      {getMetricDescription(metrics.recall, 'recall', t)}
                                    </div>
                                  </div>

                                  <div className="flex items-start justify-between">
                                    <div className="space-y-2">
                                      <div className="text-sm font-medium text-medium-emphasis">
                                        {t('RESULT')}
                                      </div>
                                      <div
                                        className={`text-base font-medium ${
                                          metrics.hit_rate ? 'text-green-600' : 'text-red-600'
                                        }`}
                                      >
                                        {metrics.hit_rate ? t('YES') : t('NO')}
                                      </div>
                                    </div>
                                    <div className="max-w-[200px] text-sm text-muted-foreground">
                                      {metrics.hit_rate
                                        ? t('HIT_RATE_YES_DESCRIPTION')
                                        : t('HIT_RATE_NO_DESCRIPTION')}
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
                <div className="mb-4 space-y-1 rounded-lg border-l-4 border-primary bg-primary/5 py-2.5 pl-3.5 dark:border-primary dark:bg-primary/20">
                  <p className="text-xs text-primary dark:text-blue-200">
                    <span className="font-semibold">*</span> {t('ADEQUACY_SCORE_NOTE')}
                  </p>
                  {hasValidGroundTruth && (
                    <p className="text-xs text-primary dark:text-blue-200">
                      <span className="font-semibold">**</span> {t('QUALITY_SCORE_NOTE')}
                    </p>
                  )}
                </div>
                <TabsContent
                  value={activeTab}
                  className="flex h-full flex-1 flex-col overflow-hidden"
                >
                  <div className="mb-4 flex-shrink-0 space-y-3">
                    <p className="text-sm">
                      {t('QUERY_FOR')}{' '}
                      <span className="font-semibold">
                        &quot;
                        {results.find((r) => r.query_type === activeTab)?.query || vectorQuery}
                        &quot;
                      </span>
                    </p>
                  </div>

                  <div className="grid flex-1 auto-rows-min grid-cols-1 gap-4 overflow-y-auto pb-4 lg:grid-cols-2">
                    {(() => {
                      const currentResult = results.find((r) => r.query_type === activeTab);
                      const documentsToShow = currentResult?.documents || [];

                      if (documentsToShow.length === 0) {
                        return (
                          <div className="col-span-full flex items-center justify-center rounded border border-dashed py-12">
                            <p className="text-medium-emphasis">
                              {t('NO_DOCUMENTS_FOR_QUERY')}
                            </p>
                          </div>
                        );
                      }

                      return documentsToShow.map((doc, idx) => {
                        const chunk = {
                          id: `${String(idx + 1).padStart(3, '0')}`,
                          title: doc.text.slice(0, 100) + '...',
                          content: doc.text.slice(0, 200) + '...',
                          tokens: doc.tokens,
                          layers: doc.chars,
                          score: doc.score,
                          tags: doc.keywords || [],
                        };
                        return (
                          <Card
                            key={idx}
                            className="flex cursor-pointer flex-col transition-shadow hover:shadow-md"
                            onClick={() =>
                              setSelectedChunk({
                                ...chunk,
                                content: doc.text,
                              })
                            }
                          >
                            <CardContent className="flex flex-col items-start gap-4 overflow-hidden">
                              <div className="flex flex-wrap gap-3">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex cursor-help items-center gap-1.5 rounded-md bg-muted px-2 py-1">
                                      <Crop className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">{chunk.id}</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('SECTION_TOOLTIP')}</p>
                                  </TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex cursor-help items-center gap-1.5 rounded-md bg-muted px-2 py-1">
                                      <Type className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">{chunk.tokens}</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('TOKENS_TOOLTIP')}</p>
                                  </TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex cursor-help items-center gap-1.5 rounded-md bg-muted px-2 py-1">
                                      <Layers className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">{chunk.layers}</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('DEPTH_TOOLTIP')}</p>
                                  </TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex cursor-help items-center gap-1.5 rounded-md bg-muted px-2 py-1">
                                      <Medal className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">
                                        {chunk.score.toFixed(2)}
                                      </span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('CHUNK_QUALITY_TOOLTIP')}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>

                              <div className="w-full flex-1">
                                <p
                                  className="break-all text-sm leading-relaxed"
                                  style={{
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                  }}
                                >
                                  {t('CHUNK_CONTENT_PREVIEW', { id: chunk.id, content: chunk.content })}
                                </p>
                                <p className="mt-2 text-sm text-muted-foreground">...</p>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                {chunk.tags.map((tag, tagIdx) => (
                                  <span
                                    key={tagIdx}
                                    className="break-all rounded-md bg-muted px-2.5 py-1 text-sm text-muted-foreground"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      });
                    })()}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center rounded border border-dashed">
              <p className="text-medium-emphasis">{t('QUERY_RESULTS_PLACEHOLDER')}</p>
            </div>
          )}
          <ChunkDetailsModal chunk={selectedChunk} onClose={() => setSelectedChunk(null)} />
        </CardContent>
      </Card>
    </div>
  );
};
